/**
 * Ejecución de una revisión y emisión del acta.
 *
 * Es la pantalla con más consecuencia legal del ERP: de aquí sale el acta
 * del anexo II §5, que es el documento que el titular enseña cuando viene
 * una inspección.
 *
 * Tres cosas que la gobiernan y que no son de interfaz:
 *
 * 1. **«Pendiente» no es «no revisado».** Un equipo sin tocar y uno
 *    inaccesible con su motivo son cosas distintas en el acta. Confundirlos
 *    fue un fallo real de este proyecto: la revisión salía al 100 % con
 *    equipos que nadie había visitado.
 * 2. **Un equipo no revisado exige motivo.** Se comprueba aquí, en el
 *    dominio y en un CHECK de la base. Aquí sirve para no dejar guardar
 *    algo que el servidor va a rechazar.
 * 3. **El acta se valida antes de generarse**, y cada incumplimiento viene
 *    con su artículo. Un acta no conforme no es un aviso: es un documento
 *    que no vale.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSession } from '../session/session-context.js';
import { openDownload } from '../session/open-download.js';
import { ApiError } from '../session/api-error.js';
import { PdfButton } from './pdf-button.js';

interface Line {
  id: string;
  equipment_id: string;
  internal_code: string;
  equipment_type: string;
  equipment_status: string;
  snapshot_location: string | null;
  reviewed: boolean;
  result_code: string | null;
  not_reviewed_reason: string | null;
  not_reviewed_notes: string | null;
  notes: string | null;
  answered_items: string;
  operations: string[];
  /** Obligaciones vivas del equipo, para declarar cuáles se atienden. */
  pending_operations: {
    ruleCode: string;
    operationName: string;
    articleRef: string;
    dueDate: string | null;
    overdue: boolean;
  }[];
}

interface ActaInfo {
  /** Sello de la versión del acta: identifica el documento a renderizar. */
  sealHash: string | null;
}

interface InspectionDetail {
  id: string;
  number: string;
  status: string;
  inspection_type: string;
  performed_on: string;
  customer_id: string;
  customer_name: string;
  site_id: string;
  site_name: string;
  site_code: string;
  technician_name: string | null;
  planned_equipment: number;
  lines: Line[];
}

interface Progress {
  plannedEquipment: number;
  reviewed: number;
  notReviewed: number;
  pending: number;
  withDefects: number;
  blocking: number;
  percentComplete: number;
  notReviewedByReason: Record<string, number>;
}

interface ActaIssue {
  field: string;
  articleRef: string;
  message: string;
  equipmentId?: string;
}

interface ActaValidation {
  conforme: boolean;
  issues: ActaIssue[];
}

/** Los seis motivos del `CHECK` de la migración 0013. */
const NOT_REVIEWED_REASONS = [
  { value: 'INACCESIBLE', label: 'Inaccesible' },
  { value: 'NO_LOCALIZADO', label: 'No localizado' },
  { value: 'ZONA_CERRADA', label: 'Zona cerrada' },
  { value: 'EN_TALLER', label: 'En taller' },
  { value: 'RETIRADO_POR_CLIENTE', label: 'Retirado por el cliente' },
  { value: 'SIN_TIEMPO', label: 'Sin tiempo' },
];

/**
 * Las clasificaciones de resultado vienen del servidor.
 *
 * Estaban escritas aquí —`CONFORME`, `NO_CONFORME_LEVE`…— y ninguna existía
 * en el catálogo de la empresa, que trae `CORRECTO`, `OBSERVACION`,
 * `DEFECTO`… Resultado: la pantalla no podía guardar ni un resultado, y el
 * error llegaba del servidor al pulsar guardar.
 *
 * Son configurables por empresa, así que no pueden vivir en el código.
 */
interface Classification {
  code: string;
  label: string;
  severity: number;
  blocks_closure: boolean;
}

export function InspectionRunScreen(): React.ReactNode {
  const { api } = useSession();
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<InspectionDetail | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [validation, setValidation] = useState<ActaValidation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [signing, setSigning] = useState<'MAINTAINER' | 'PROPERTY' | null>(null);
  const [signerName, setSignerName] = useState('');
  const [signerPosition, setSignerPosition] = useState('');
  const [missingSignatures, setMissingSignatures] = useState<string[] | null>(null);
  const [actaSeal, setActaSeal] = useState<string | null>(null);
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [sent, setSent] = useState<{ status: string; attached: boolean } | null>(null);

  const reload = useCallback(async () => {
    const [next, prog, cls] = await Promise.all([
      api.get<InspectionDetail>(`/inspections/${id}`),
      api.get<Progress>(`/inspections/${id}/progress`),
      api
        .get<{ items: Classification[] }>('/regulations/classifications')
        .catch(() => ({ items: [] as Classification[] })),
    ]);
    setDetail(next);
    setProgress(prog);
    setClassifications(cls.items);

    // El sello sólo existe si el acta está generada, y es lo que
    // identifica QUÉ documento se renderiza.
    if (next.status === 'ACTA_GENERADA') {
      const info = await api
        .get<ActaInfo>(`/inspections/${id}/acta/seal`)
        .catch(() => ({ sealHash: null }));
      setActaSeal(info.sealHash);
    }
  }, [api, id]);

  useEffect(() => {
    let cancelled = false;

    reload()
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar la revisión.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reload]);

  async function run(label: string, action: () => Promise<unknown>): Promise<void> {
    setBusy(label);
    setError(null);
    try {
      await action();
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'La operación no se ha podido completar.');
    } finally {
      setBusy(null);
    }
  }

  async function saveResult(line: Line, values: ResultDraft): Promise<void> {
    await run(line.id, async () => {
      await api.post(`/inspections/${id}/equipment/${line.equipment_id}/result`, {
        reviewed: values.reviewed,
        resultCode: values.reviewed ? values.resultCode : null,
        notReviewedReason: values.reviewed ? null : values.reason,
        notReviewedNotes: values.reviewed ? null : values.reasonNotes || null,
        outOfService:
          classifications.find((c) => c.code === values.resultCode)?.blocks_closure ?? false,
        // Qué obligaciones se atienden. Sin esto el cierre tenía que
        // adivinarlo por la fecha de vencimiento.
        operations: values.operations,
        notes: values.notes || null,
      });
      setOpen(null);
    });
  }

  async function sign(role: 'MAINTAINER' | 'PROPERTY'): Promise<void> {
    if (signerName.trim() === '') return;

    await run(`sign-${role}`, async () => {
      const result = await api.post<{ complete: boolean; missing: string[] }>(
        `/inspections/${id}/acta/signatures`,
        {
          signerRole: role,
          signerName: signerName.trim(),
          ...(signerPosition.trim() !== '' ? { signerPosition: signerPosition.trim() } : {}),
        },
      );
      setMissingSignatures(result.missing);
      setSigning(null);
      setSignerName('');
      setSignerPosition('');
    });
  }

  /**
   * Envía el acta al titular (art. 17.f).
   *
   * Es lo que convierte un PDF guardado en cumplimiento: la norma no pide
   * generar el documento, pide **comunicárselo al titular**, y que quede
   * constancia.
   */
  async function sendToOwner(): Promise<void> {
    await run('send', async () => {
      setSent(
        await api.post<{ status: string; attached: boolean }>(
          `/communications/inspections/${id}/acta`,
        ),
      );
    });
  }

  async function validate(): Promise<void> {
    await run('validate', async () => {
      setValidation(await api.get<ActaValidation>(`/inspections/${id}/acta/validation`));
    });
  }

  if (loading) {
    return (
      <section className="screen">
        <p className="table__loading">Cargando la revisión…</p>
      </section>
    );
  }

  const editable = detail?.status === 'EN_CURSO' || detail?.status === 'PARCIAL';

  return (
    <section className="screen">
      <header className="detail__head">
        <div className="detail__title">
          <h1 className="screen__title">{detail?.number ?? 'Revisión'}</h1>
          <span className={`pill pill--${detail?.status === 'CERRADA' ? 'ok' : 'warn'}`}>
            {detail?.status}
          </span>
        </div>
        <p className="detail__subtitle">
          {detail && (
            <>
              <Link to={`/centros/${detail.site_id}`}>{detail.site_name}</Link> ·{' '}
              {detail.customer_name} · {detail.performed_on}
              {detail.technician_name && ` · ${detail.technician_name}`}
            </>
          )}
        </p>
      </header>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      {progress && (
        <div className="cards">
          <div className="card">
            <p className="card__label">Revisados</p>
            <p className="card__value">
              {progress.reviewed} / {progress.plannedEquipment}
            </p>
            <p className="card__meta">{progress.percentComplete} % completado</p>
          </div>
          <div className="card">
            <p className="card__label">Pendientes</p>
            <p className="card__value">{progress.pending}</p>
            {/* La distinción que costó un fallo: sin tocar NO es «no
                revisado», y mientras haya pendientes la revisión no cierra. */}
            <p className="card__meta">Sin atender todavía. No son «no revisados».</p>
          </div>
          <div className="card">
            <p className="card__label">No revisados</p>
            <p className="card__value">{progress.notReviewed}</p>
            <p className="card__meta">Con motivo. Constan en el acta (§37).</p>
          </div>
          <div className={`card${progress.blocking > 0 ? ' card--alert' : ''}`}>
            <p className="card__label">Con defecto</p>
            <p className="card__value">{progress.withDefects}</p>
            {progress.blocking > 0 && (
              <p className="card__warn">⚠ {progress.blocking} impiden cerrar</p>
            )}
          </div>
        </div>
      )}

      <h2 className="screen__subtitle">Equipos</h2>

      <div className="scroller">
        <table className="table__grid table__grid--compact">
          <thead>
            <tr>
              <th>Equipo</th>
              <th>Tipo</th>
              <th>Ubicación</th>
              <th>Estado</th>
              <th>Resultado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {detail?.lines.map((line) => (
              <LineRow
                key={line.id}
                line={line}
                editable={editable ?? false}
                busy={busy === line.id}
                open={open === line.id}
                classifications={classifications}
                onToggle={() => setOpen(open === line.id ? null : line.id)}
                onSave={(values) => void saveResult(line, values)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="screen__subtitle">Acta</h2>

      <p className="screen__note">
        El acta se valida <strong>antes</strong> de generarse. Cada incumplimiento viene con su
        artículo: un acta no conforme no es un aviso, es un documento que no vale.
        {detail?.status === 'ACTA_GENERADA' && (
          <>
            {' '}
            El documento se abre en una pestaña nueva, maquetado en A4 y listo para imprimir o
            guardar como PDF. Lleva impreso el <strong>sello de la versión</strong> de la que
            procede: es lo que permite comprobar dentro de cinco años que ese papel es ése.
          </>
        )}
      </p>

      <div className="form__actions">
        <button
          className="button button--quiet"
          type="button"
          disabled={busy !== null}
          onClick={() => void validate()}
        >
          {busy === 'validate' ? 'Validando…' : 'Validar el acta'}
        </button>

        {editable && (
          <button
            className="button button--quiet"
            type="button"
            disabled={busy !== null || (progress?.pending ?? 0) > 0}
            title={
              (progress?.pending ?? 0) > 0
                ? 'Quedan equipos sin atender: cada uno necesita resultado o motivo.'
                : undefined
            }
            onClick={() => void run('complete', () => api.post(`/inspections/${id}/complete`))}
          >
            {busy === 'complete' ? 'Cerrando…' : 'Cerrar la ejecución'}
          </button>
        )}

        {detail?.status === 'ACTA_GENERADA' && (
          <a
            className="button button--primary button--small"
            href={`/v1/inspections/${id}/acta/print`}
            target="_blank"
            rel="noreferrer"
          >
            Ver e imprimir el acta
          </a>
        )}

        {detail?.status === 'ACTA_GENERADA' && actaSeal !== null && (
          <PdfButton kind="ACTA" entityId={id ?? ''} sourceHash={actaSeal} label="Generar el PDF" />
        )}

        {/* El informe de incidencias no espera al acta: en cuanto la revisión
            está ejecutada ya se sabe qué está mal, y es lo que el cliente
            necesita para decidir la reparación. Se rehace si cambian las
            anomalías, por eso la huella lleva el estado y las líneas. */}
        {detail !== null && detail.status !== 'BORRADOR' && (
          <PdfButton
            kind="ANOMALY_REPORT"
            entityId={id ?? ''}
            sourceHash={`${id ?? ''}-${detail.lines.length}-${detail.status}`}
            label="Informe de incidencias"
          />
        )}

        {/* El certificado es lo que se entrega al cliente al terminar: una
            hoja con el recuento por familia. El acta sigue siendo el documento
            que prueba ante un inspector, y va por su lado. */}
        {detail !== null && detail.status !== 'BORRADOR' && (
          <PdfButton
            kind="MAINTENANCE_CERTIFICATE"
            entityId={id ?? ''}
            sourceHash={`${id ?? ''}-${detail.lines.length}-${detail.status}`}
            label="Certificado"
          />
        )}

        {/* El respaldo ante inspección: lo que se comprobó en cada equipo. No
            se entrega al cliente salvo que lo pida; se guarda. */}
        {detail !== null && detail.status !== 'BORRADOR' && (
          <PdfButton
            kind="CHECKLIST_REPORT"
            entityId={id ?? ''}
            sourceHash={`${id ?? ''}-${detail.lines.length}-${detail.status}`}
            label="Listas de comprobación"
          />
        )}

        {detail?.status === 'ACTA_GENERADA' && (
          <button
            className="button button--quiet"
            type="button"
            disabled={busy !== null}
            onClick={() => void sendToOwner()}
          >
            {busy === 'send' ? 'Enviando…' : 'Enviar al titular'}
          </button>
        )}

        {/*
          Las etiquetas del art. 17.g se imprimen aquí, no en el listado de
          equipos. Es el único momento en que existen las dos fechas que la
          etiqueta exige —la de la operación y la de la próxima revisión— y
          es donde está el técnico: acaba de firmar el acta y tiene delante
          los extintores a los que hay que pegarlas. Obligarle a volver al
          inventario y seleccionar a mano los mismos equipos es la clase de
          paso que se salta, y una etiqueta que no se pega es un
          incumplimiento.
        */}
        {detail?.status === 'ACTA_GENERADA' && detail.lines.length > 0 && (
          <button
            className="button button--quiet"
            type="button"
            onClick={() =>
              void openDownload(
                api,
                `/v1/equipment/labels/print?ids=${detail.lines
                  .map((line) => line.equipment_id)
                  .join(',')}`,
              )
            }
          >
            Imprimir etiquetas ({detail.lines.length})
          </button>
        )}

        <button
          className="button button--primary"
          type="button"
          disabled={busy !== null || validation?.conforme !== true}
          title={
            validation?.conforme !== true
              ? 'Primero hay que validar el acta y que salga conforme.'
              : undefined
          }
          onClick={() => void run('acta', () => api.post(`/inspections/${id}/acta`))}
        >
          {busy === 'acta' ? 'Generando…' : 'Generar el acta'}
        </button>
      </div>

      {detail?.status === 'ACTA_GENERADA' && (
        <>
          <h2 className="screen__subtitle">Firmas</h2>
          <p className="screen__note">
            {/* D-06: el acta la firman las dos partes. Con una sola firma
                el documento existe pero no acredita la conformidad del
                titular con lo que se hizo. */}
            El acta la firman <strong>las dos partes</strong>: la empresa mantenedora y la propiedad.
            {missingSignatures !== null && missingSignatures.length > 0 && (
              <> Falta: {missingSignatures.join(', ')}.</>
            )}
            {missingSignatures !== null && missingSignatures.length === 0 && (
              <> ✓ Firmada por las dos partes.</>
            )}
          </p>

          <div className="form__actions">
            <button
              className="button button--quiet"
              type="button"
              disabled={busy !== null}
              onClick={() => setSigning('MAINTAINER')}
            >
              Firmar como mantenedora
            </button>
            <button
              className="button button--quiet"
              type="button"
              disabled={busy !== null}
              onClick={() => setSigning('PROPERTY')}
            >
              Firmar como propiedad
            </button>
          </div>

          {signing !== null && (
            <div className="line-form">
              <p className="field__help">
                {signing === 'MAINTAINER'
                  ? 'Quien firma por la empresa mantenedora.'
                  : 'Quien firma por la propiedad o el titular de la instalación.'}
              </p>
              <div className="line-form__row">
                <label className="line-form__grow">
                  Nombre y apellidos
                  <input value={signerName} onChange={(e) => setSignerName(e.target.value)} />
                </label>
                <label className="line-form__grow">
                  Cargo
                  <input value={signerPosition} onChange={(e) => setSignerPosition(e.target.value)} />
                </label>
              </div>
              <div className="form__actions">
                <button
                  className="button button--primary button--small"
                  type="button"
                  disabled={busy !== null || signerName.trim() === ''}
                  onClick={() => void sign(signing)}
                >
                  {busy === `sign-${signing}` ? 'Firmando…' : 'Firmar'}
                </button>
                <button
                  className="button button--quiet button--small"
                  type="button"
                  onClick={() => setSigning(null)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {sent !== null && (
        <p
          className={`screen__note${sent.status === 'ENVIADA' ? '' : ' screen__note--strong'}`}
          role="status"
        >
          {sent.status === 'ENVIADA' && (
            <>
              ✓ Acta comunicada al titular{sent.attached ? ' con el PDF adjunto' : ' sin adjunto: el PDF todavía se está generando'}.
              La comunicación queda registrada como evidencia del art. 17.f.
            </>
          )}
          {sent.status === 'AMBIGUA' && (
            <>
              {/* Ni «enviado» ni «fallido»: puede haber llegado. Reintentar
                  a ciegas duplicaría la comunicación. */}
              ⚠ <strong>No se sabe si llegó.</strong> El servidor de correo aceptó el mensaje y no
              confirmó. Compruébelo antes de reenviar: reintentar a ciegas duplicaría la
              comunicación al titular. El intento consta en el historial.
            </>
          )}
          {sent.status === 'FALLIDA' && (
            <>⚠ No se pudo enviar. El intento consta en el historial y se puede reintentar.</>
          )}
        </p>
      )}

      {validation && (
        <div className={`screen__note${validation.conforme ? '' : ' screen__note--strong'}`}>
          {validation.conforme ? (
            <>✓ El acta cumple el contenido del anexo II §5. Se puede generar.</>
          ) : (
            <>
              <strong>El acta todavía no cumple el anexo II §5.</strong> Falta:
              <ul className="issues">
                {validation.issues.map((issue, index) => (
                  <li key={`${issue.field}-${index}`}>
                    {issue.message} <em>({issue.articleRef})</em>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════

interface ResultDraft {
  reviewed: boolean;
  resultCode: string;
  /** Códigos de regla que esta visita atiende (anexo II §5.b). */
  operations: string[];
  reason: string;
  reasonNotes: string;
  notes: string;
}

function LineRow({
  line,
  editable,
  busy,
  open,
  classifications,
  onToggle,
  onSave,
}: {
  line: Line;
  editable: boolean;
  busy: boolean;
  open: boolean;
  classifications: Classification[];
  onToggle: () => void;
  onSave: (values: ResultDraft) => void;
}): React.ReactNode {
  const [draft, setDraft] = useState<ResultDraft>({
    reviewed: line.reviewed,
    resultCode: line.result_code ?? classifications[0]?.code ?? '',
    // Vienen marcadas las que ya estaban vencidas: es lo que una visita de
    // mantenimiento atiende. El técnico añade o quita.
    operations:
      line.operations.length > 0
        ? line.operations
        : line.pending_operations.filter((o) => o.overdue).map((o) => o.ruleCode),
    reason: line.not_reviewed_reason ?? '',
    reasonNotes: line.not_reviewed_notes ?? '',
    notes: line.notes ?? '',
  });

  // Sin motivo no se puede guardar: es lo que evita que un equipo sin
  // tocar figure como atendido. El servidor lo rechazaría igual, pero
  // enterarse aquí ahorra el viaje.
  const missingReason = !draft.reviewed && draft.reason === '';

  const state = line.reviewed
    ? {
        label: line.result_code ?? 'Revisado',
        tone: (classifications.find((c) => c.code === line.result_code)?.severity ?? 1) === 0 ? 'ok' : 'warn',
      }
    : line.not_reviewed_reason !== null
      ? { label: `No revisado · ${line.not_reviewed_reason}`, tone: 'off' }
      : { label: 'Pendiente', tone: 'warn' };

  return (
    <>
      <tr>
        <td>{line.internal_code}</td>
        <td>{line.equipment_type}</td>
        <td>{line.snapshot_location ?? '—'}</td>
        <td>{line.equipment_status}</td>
        <td>
          <span className={`pill pill--${state.tone}`}>{state.label}</span>
        </td>
        <td>
          {editable && (
            <button type="button" className="button button--quiet button--small" onClick={onToggle}>
              {open ? 'Cerrar' : 'Anotar'}
            </button>
          )}
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={6}>
            <div className="line-form">
              <div className="line-form__row">
                <label>
                  <input
                    type="radio"
                    name={`rev-${line.id}`}
                    checked={draft.reviewed}
                    onChange={() => setDraft({ ...draft, reviewed: true })}
                  />
                  Revisado
                </label>
                <label>
                  <input
                    type="radio"
                    name={`rev-${line.id}`}
                    checked={!draft.reviewed}
                    onChange={() => setDraft({ ...draft, reviewed: false })}
                  />
                  No he podido revisarlo
                </label>
              </div>

              {draft.reviewed ? (
                <div className="line-form__row">
                  <label>
                    Resultado
                    <select
                      value={draft.resultCode}
                      onChange={(e) => setDraft({ ...draft, resultCode: e.target.value })}
                    >
                      {classifications.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {line.pending_operations.length > 0 && (
                    <fieldset className="line-form__ops">
                      <legend>Operaciones realizadas (anexo II §5.b)</legend>
                      {line.pending_operations.map((op) => (
                        <label key={op.ruleCode} className="line-form__op">
                          <input
                            type="checkbox"
                            checked={draft.operations.includes(op.ruleCode)}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                operations: e.target.checked
                                  ? [...draft.operations, op.ruleCode]
                                  : draft.operations.filter((c) => c !== op.ruleCode),
                              })
                            }
                          />
                          <span>
                            {op.operationName}
                            <small>
                              {' '}
                              · {op.ruleCode} · {op.articleRef}
                              {op.dueDate ? ` · vence ${op.dueDate}` : ''}
                              {op.overdue ? ' · VENCIDA' : ''}
                            </small>
                          </span>
                        </label>
                      ))}
                      <p className="lines__warn">
                        Lo que se marque aquí es lo que el acta declara como realizado y lo que da
                        por cumplida la obligación. Lo que no se marque sigue pendiente.
                      </p>
                    </fieldset>
                  )}

                  {classifications.find((c) => c.code === draft.resultCode)?.blocks_closure && (
                    <span className="lines__warn">
                      ⚠ Un equipo fuera de servicio no protege. Hay que comunicárselo al titular por
                      escrito (art. 17.f).
                    </span>
                  )}
                </div>
              ) : (
                <div className="line-form__row">
                  <label>
                    Motivo
                    <select
                      value={draft.reason}
                      onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                    >
                      <option value="">— Elija el motivo —</option>
                      {NOT_REVIEWED_REASONS.map((reason) => (
                        <option key={reason.value} value={reason.value}>
                          {reason.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="line-form__grow">
                    Detalle
                    <input
                      value={draft.reasonNotes}
                      onChange={(e) => setDraft({ ...draft, reasonNotes: e.target.value })}
                    />
                  </label>
                </div>
              )}

              <div className="line-form__row">
                <label className="line-form__grow">
                  Observaciones
                  <input
                    value={draft.notes}
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  />
                </label>
              </div>

              {missingReason && (
                <p className="field__error" role="alert">
                  Un equipo no revisado exige motivo: sin él, el acta diría que se atendió.
                </p>
              )}

              <div className="form__actions">
                <button
                  type="button"
                  className="button button--primary button--small"
                  disabled={busy || missingReason}
                  onClick={() => onSave(draft)}
                >
                  {busy ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
